import React from 'react';
import { DollarSign, FileText, Shield, Clock, CheckCircle, Phone } from 'lucide-react';
import { consultationBenefits, firmInfo } from '../mock';
import DeftForm from '../components/DeftForm';

const FreeConsultation = () => {
  return (
    <div style={{ marginTop: '70px' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--bg-card) 0%, var(--bg-page) 100%)',
        padding: '100px 0',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: 'url(https://images.unsplash.com/photo-1758518731462-d091b0b4ed0d)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: 'var(--brand-primary)',
            color: 'var(--text-inverse)',
            borderRadius: '50px',
            fontSize: '0.9rem',
            fontWeight: '600',
            marginBottom: '24px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            100% Free - No Obligation
          </div>
          <h1 className="heading-1" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
            Schedule Your Free Consultation
          </h1>
          <p className="body-large" style={{ color: 'var(--text-white)', maxWidth: '800px', margin: '0 auto 32px' }}>
            Get expert legal advice at no cost. We'll review your case, explain your rights, and outline your path to compensation.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <a href={`tel:${firmInfo.phone}`} className="btn-primary" style={{ fontSize: '1.1rem', padding: '1.2em 2.5em' }}>
              <Phone size={20} style={{ marginRight: '8px' }} />
              Call {firmInfo.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ padding: '96px 0', background: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '64px', textAlign: 'center' }}>
            Why Schedule a Free Consultation?
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {consultationBenefits.map((benefit, index) => {
              const IconComponent = {
                'DollarSign': DollarSign,
                'FileText': FileText,
                'Shield': Shield,
                'Clock': Clock
              }[benefit.icon] || DollarSign;

              return (
                <div
                  key={benefit.id}
                  style={{
                    padding: '40px',
                    background: 'var(--bg-card)',
                    borderRadius: '16px',
                    border: '2px solid var(--border-light)',
                    textAlign: 'center',
                    animation: `fadeIn 0.6s ease-out ${index * 0.1}s backwards`
                  }}
                >
                  <div style={{
                    width: '70px',
                    height: '70px',
                    background: 'var(--brand-primary)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px'
                  }}>
                    <IconComponent size={32} color="var(--text-inverse)" />
                  </div>
                  <h3 className="heading-4" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>
                    {benefit.title}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Consultation Form Section */}
      <section style={{ padding: '96px 0', background: 'var(--bg-card)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>
                Request Your Free Consultation
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                Fill out the form below and we'll contact you within 2 hours to schedule your consultation
              </p>
            </div>

            <div style={{
              background: 'var(--bg-page)',
              padding: '48px',
              borderRadius: '16px',
              border: '2px solid var(--border-light)'
            }}>
              {/* DeftForm Embed */}
              <DeftForm />

              <div style={{
                marginTop: '24px',
                padding: '20px',
                background: 'var(--bg-card)',
                borderRadius: '8px',
                border: '1px solid var(--brand-primary)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <CheckCircle size={24} color="var(--brand-primary)" />
                  <strong style={{ color: 'var(--brand-primary)' }}>No Risk Guarantee</strong>
                </div>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.6' }}>
                  This consultation is completely free with no obligation. We work on a contingency fee basis - you don't pay unless we win your case. Your information is confidential and protected by attorney-client privilege.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section style={{ padding: '96px 0', background: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '64px', textAlign: 'center' }}>
            What to Expect During Your Consultation
          </h2>
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '32px'
          }}>
            {[
              {
                step: '1',
                title: 'Case Review',
                description: 'We\'ll listen to your story and review the details of your injury and how it occurred.'
              },
              {
                step: '2',
                title: 'Legal Analysis',
                description: 'We\'ll explain your legal rights, potential claims, and the strength of your case.'
              },
              {
                step: '3',
                title: 'Strategy Discussion',
                description: 'We\'ll outline the steps involved in pursuing your claim and what to expect throughout the process.'
              },
              {
                step: '4',
                title: 'Questions & Answers',
                description: 'You\'ll have time to ask questions and get clear answers about your situation.'
              }
            ].map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: '24px',
                  alignItems: 'flex-start',
                  padding: '32px',
                  background: 'var(--bg-card)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-light)',
                  animation: `fadeIn 0.6s ease-out ${index * 0.1}s backwards`
                }}
              >
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: 'var(--brand-primary)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '1.5rem',
                  fontWeight: '900',
                  color: 'var(--text-inverse)'
                }}>
                  {item.step}
                </div>
                <div>
                  <h3 style={{ color: 'var(--brand-primary)', marginBottom: '8px', fontSize: '1.25rem', fontWeight: '600' }}>
                    {item.title}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FreeConsultation;
